import type { ReactNode } from "react";
import { Pressable } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../theme";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { View } from "../View";
import type {
  NavSidePanelDisclosureIconRenderProps,
  NavSidePanelItem,
  NavSidePanelItemContentRenderProps,
  NavSidePanelParentItem,
} from "./NavSidePanel.types";

export function isParentItem(item: NavSidePanelItem): item is NavSidePanelParentItem {
  return item.children !== undefined;
}

export function collectDefaultExpandedItemIds(items: readonly NavSidePanelItem[]): readonly string[] {
  return items.flatMap((item) => {
    if (!isParentItem(item)) return [];
    const childIds = collectDefaultExpandedItemIds(item.children);
    return item.defaultExpanded === true ? [item.id, ...childIds] : childIds;
  });
}

export function getAccessibilityLabel(item: NavSidePanelItem): string | undefined {
  if (item.accessibilityLabel !== undefined) return item.accessibilityLabel;
  return typeof item.label === "string" ? item.label : undefined;
}

export function DefaultItemContent({
  item,
  expanded,
  hasChildren,
  selected,
  disabled,
  minimized,
  depth,
  renderDisclosureIcon,
  onChevronPress,
}: NavSidePanelItemContentRenderProps & {
  readonly renderDisclosureIcon?: (props: NavSidePanelDisclosureIconRenderProps) => ReactNode;
  readonly onChevronPress?: (() => void) | undefined;
}) {
  const { theme } = useUnistyles();
  const labelColor = theme.colors.text;
  const mutedColor = selected ? theme.colors.interactive.controls.selected_midground : theme.colors.textMuted;
  const contentOpacity = disabled ? 0.45 : 1;
  const parentItem = isParentItem(item) ? item : undefined;

  if (minimized) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" style={{ opacity: contentOpacity }}>
        {item.icon !== undefined ?
          <View style={styles.itemIcon}>{item.icon}</View>
        : typeof item.label === "string" ?
          <Text textStyle="sm/semibold" style={{ color: labelColor }}>
            {item.label.slice(0, 1)}
          </Text>
        : null}
      </View>
    );
  }

  return (
    <View flexDirection="row" alignItems="center" gap="md" flex={1} style={{ opacity: contentOpacity }}>
      {item.icon !== undefined && <View style={styles.itemIcon}>{item.icon}</View>}
      <View flex={1} style={styles.itemText}>
        <Text textStyle="sm/regular" style={{ color: labelColor }} lineClamp={1}>
          {item.label}
        </Text>
        {item.secondaryLabel !== undefined && (
          <Text textStyle="xs/regular" style={{ color: mutedColor }} lineClamp={1}>
            {item.secondaryLabel}
          </Text>
        )}
      </View>
      {item.trailing !== undefined && <View style={styles.itemTrailing}>{item.trailing}</View>}
      {hasChildren &&
        parentItem !== undefined &&
        (onChevronPress ?
          <Pressable
            onPress={onChevronPress}
            hitSlop={4}
            style={styles.disclosureButton}
            accessibilityRole="button"
            accessibilityLabel={expanded ? "Collapse" : "Expand"}
          >
            {renderDisclosureIcon ?
              renderDisclosureIcon({ item: parentItem, depth, expanded, selected, disabled, minimized })
            : <Icon name={expanded ? "chevronUp" : "chevronDown"} size={14} color={mutedColor} />}
          </Pressable>
        : <View style={styles.itemTrailing}>
            {renderDisclosureIcon ?
              renderDisclosureIcon({ item: parentItem, depth, expanded, selected, disabled, minimized })
            : <Icon name={expanded ? "chevronUp" : "chevronDown"} size={18} color={mutedColor} />}
          </View>)}
    </View>
  );
}

export const styles = StyleSheet.create((theme) => ({
  panel: {
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  header: {
    padding: theme.spacing["2xl"],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderCard,
  },
  minimizeButton: {
    width: 36,
    height: 36,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    alignItems: "center",
    justifyContent: "center",
  },
  itemsRegion: {
    minHeight: 0,
    paddingVertical: theme.spacing.md,
  },
  scrollContent: {
    paddingVertical: theme.spacing.md,
  },
  footer: {
    paddingTop: theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderCard,
  },
  footerSlot: {
    paddingTop: theme.spacing.md,
  },
  item: {
    minHeight: 44,
    paddingRight: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderRadius: theme.spacing.md,
    flexDirection: "row",
    alignItems: "center",
  },
  itemMinimized: {
    justifyContent: "center",
    paddingRight: theme.spacing.lg,
  },
  itemIcon: {
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  itemText: {
    minWidth: 0,
  },
  itemTrailing: {
    flexShrink: 0,
  },
  disclosureButton: {
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    alignItems: "center",
    justifyContent: "center",
  },
  children: {
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing["2xl"],
  },
}));
